import { useEffect, useRef, useState } from 'react';
import { 
  Autocomplete, 
  IconArrowDropDown, 
  IconArrowDropUp, 
  Input, 
  KEY_VALUES, 
  MenuListItem } from 'cdk-radial';

type OptionType = { label: string; value?: string; isDefault?: boolean; disabled?: boolean };
type MyAutocompleteProps = {
  id: string;
  defaultOptions: OptionType[];
  selected: OptionType;
  onSelectionChange: (value: string) => void;
  onClear: () => void;
};

export const UserStores = ({ 
  id, 
  defaultOptions,
  selected,
  onSelectionChange,
  onClear}: MyAutocompleteProps) => {
  const targetRef = useRef<HTMLInputElement>(null);
  const dimensionRef = useRef<HTMLDivElement>(null);
  
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(selected);
  const [inputValue, setInputValue] = useState(selected.label);
  const [options, setOptions] = useState(defaultOptions);
  
  const { BACKSPACE, ENTER, SPACE, TAB } = KEY_VALUES;
  
  useEffect(() => {
      setInputValue(selected.label);
      setOptions(defaultOptions);
  }, [selected, defaultOptions]);

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const handleSelect = (option: OptionType) => {
    setSelectedOption(option);
    setInputValue(option.label);
    handleClose();
    onSelectionChange(option.value || '');
    targetRef.current?.focus();
  };

  const findOptions = (event: any) => {
    const inputText = event.target.value;
    setInputValue(inputText);
    setIsOpen(true);
    const textLength = inputText.length;
    const results = options.filter((opt: any) => {
      return inputText.toLowerCase() === opt.label.substring(0, textLength).toLowerCase();
    });
    if (results.length > 0) {
      setOptions(results);
    } else {
      setOptions([{ label: 'No Results', disabled: true }]);
    }
    if (textLength === 0) {
      setOptions(defaultOptions);
    }
  };

  const handleClear = (event: any) => {
    event.preventDefault();
    setInputValue('');
    setSelectedOption({ label: '' });
    setOptions(defaultOptions);
    onClear();
  };

  const handleMenu = () => setIsOpen((isOpen) => !isOpen);

  const handleKeyDown = (e: any) => {
    if (e.key === ENTER) {
      if (inputValue.length > 0 && options.length > 0 && !options[0].disabled) {
        setSelectedOption(options[0]);
        setInputValue(options[0].label);
        handleMenu();
      }
    }
    if (e.key === SPACE) {
      handleMenu();
    }
    if (e.key === BACKSPACE && selectedOption.label !== '') {
      handleClear(e);
    }
    if (e.key === TAB && selectedOption.label === '') {
      setInputValue('');
      setOptions(defaultOptions);
    }
  };

  return (
    <>
      <div style={{ width: '360px',marginLeft:'20px',marginTop:'20px' }} data-testid="user-store-autocomplete-menu">
        <Input
          autoComplete="off"
          dataTestId="user-store-autocomplete-menu"
          hasClearButton={selectedOption.label !== ''}
          icon={isOpen ? <IconArrowDropUp /> : <IconArrowDropDown />}
          id={id}
          label="Store"
          labelRef={dimensionRef}
          name="typeahead-dropdown"
          onChange={findOptions}
          onClick={handleMenu}
          onInputClear={handleClear}
          onKeyDown={handleKeyDown}
          placeholder="Search"
          ref={targetRef}
          value={inputValue}
          aria-expanded={isOpen}
          aria-controls="user-store-autocomplete-options"
          aria-autocomplete="list"
        />
      </div>
      <Autocomplete
        dataTestId="user-store-autocomplete-menu"
        dimensionRef={dimensionRef}
        labelRef={targetRef}
        style={{ minWidth: '360px', zIndex:'10000' }}
        onClose={handleClose}
        onChange={findOptions}
        onOpen={handleOpen}
        isOpen={isOpen}
        onUnselect={selectedOption.label === ''}
        placement="bottom-start"
        isAuto={false}
      >
        {options.map((option: any, ind: any) => (
          <MenuListItem
            dataTestId={`user-store-autocomplete-menu-list-item-${ind}`}
            key={`${ind}-${option.label}`}
            isSelected={selectedOption.label === option.label}
            onClick={() => handleSelect(option)}
            isDisabled={option.disabled}
            aria-disabled={option.disabled}
            aria-selected={selected.label === option.label}
          >
            {option.label}
          </MenuListItem>
        ))}
      </Autocomplete>
    </>
  );
};